import React, { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useOfferForm } from '../utils/useOfferForm';
import { Clock, Coffee, LockKeyhole, Mail, MapPin, Phone, Smartphone, Star, UserRound, Utensils } from 'lucide-react';
import './BigBreakfastPage.css';
import { campaignSchema as bigBreakfastSchema } from '../utils/campaignSchema';

export { bigBreakfastSchema };

const benefits = [
  { Icon: Utensils, lines: ['Delicious', 'big breakfast'] },
  { Icon: Clock, lines: ['Served 7 days', 'a week'] },
  { Icon: Coffee, lines: ['Served from', '9am to 11.30am'] },
  { Icon: Star, lines: ['The Creamery', 'experience'] },
];

const BigBreakfastPage: React.FC<{ thankYou?: boolean }> = ({ thankYou = false }) => {
  const location = useLocation();
  const { register, handleSubmit, onSubmit, isPending, submitError, formState: { errors } } = useOfferForm('BB10');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = thankYou ? 'Thank You | The Creamery Big Breakfast' : '10% Off The Big Breakfast | The Creamery';
    return () => { document.title = previousTitle; };
  }, [thankYou]);

  if (thankYou && location.state?.offerQueued !== 'BB10') return <Navigate to="/BB10" replace />;

  return (
    <main className={`bb-page${thankYou ? ' bb-thanks' : ''}`}>
      <div className="bb-poster">
        <header className="bb-hero">
          <img className="bb-breakfast" src="/images/food/breakfast-full.webp" alt="The Creamery's cooked breakfast with sausages, bacon, egg, black pudding, beans, tomatoes and toast" loading="eager" />
          <Link to="/" className="bb-brand" aria-label="The Creamery home"><img src="/logo.webp" alt="The Creamery — Sports Bar, Restaurant, Snooker, Pool" /></Link>
          {!thankYou && <div className="bb-headline">
            <h1><span className="bb-discount">Get <strong>10%</strong> off</span><span className="bb-ribbon">★ The Creamery ★</span><span className="bb-big">Big Breakfast</span></h1>
            <p className="bb-tagline">A proper start to your day</p>
            <p className="bb-secret">★ Falkirk’s best kept secret ★</p>
          </div>}
          {!thankYou && <div className="bb-hours"><Coffee aria-hidden="true" /><strong>Breakfast</strong><span>served from</span><b>9am to 11.30am</b><strong className="bb-seven">7 days</strong><span>per week</span></div>}
        </header>

        {thankYou ? <section className="bb-confirmation" aria-labelledby="bb-thanks-title">
          <h1 id="bb-thanks-title" className="bb-script">Thank You!</h1>
          <h2 className="bb-rule-heading">Your offer request has been received</h2>
          <p className="bb-intro">Your request is queued for processing. Keep an eye on your inbox and phone<br className="bb-desktop-break" /> for your exclusive 10% off The Big Breakfast offer.</p>
          <div className="bb-delivery">
            <article><span className="bb-circle"><Mail aria-hidden="true" /></span><h3>You’ll receive an email <strong>with your 10% off voucher</strong></h3><p>Once your request is processed, your voucher will be sent to your email address. It includes your unique 10% discount code, which you can use when you visit The Creamery.</p></article>
            <article><span className="bb-circle"><Smartphone aria-hidden="true" /></span><h3>You’ll also receive a text <strong>message with your discount code</strong></h3><p>We’ll send your code to your mobile number by SMS, so you can simply show it at the bar or restaurant to claim your 10% off the Big Breakfast.</p></article>
          </div>
          <p className="bb-welcome">★ We can’t wait to see you! ★</p>
          <p className="bb-team">The Creamery Team</p>
          <Link className="bb-back" to="/BB10">Back to the offer</Link>
        </section> : <section className="bb-capture" aria-labelledby="bb-form-title">
          <h2 id="bb-form-title">Enter your details below to get <strong>10% off your big breakfast!</strong></h2>
          <p>Complete the form and we’ll email you your exclusive discount.</p>
          <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label="Big Breakfast discount" aria-busy={isPending}>
            {([
              { name: 'firstName', label: 'First Name', type: 'text', autoComplete: 'given-name', Icon: UserRound },
              { name: 'lastName', label: 'Last Name', type: 'text', autoComplete: 'family-name', Icon: UserRound },
              { name: 'email', label: 'Email Address', type: 'email', autoComplete: 'email', Icon: Mail },
              { name: 'mobile', label: 'Mobile Number', type: 'tel', autoComplete: 'tel', Icon: Phone },
            ] as const).map(({ name, label, type, autoComplete, Icon }) => <div className="bb-field" key={name}>
              <label htmlFor={`bb-${name}`} className="sr-only">{label}</label>
              <div className="bb-input-wrap"><input {...register(name)} id={`bb-${name}`} type={type} autoComplete={autoComplete} placeholder={label} required aria-invalid={!!errors[name]} aria-describedby={errors[name] ? `bb-${name}-error` : undefined} /><Icon aria-hidden="true" /></div>
              {errors[name] && <p className="bb-error" id={`bb-${name}-error`} role="alert">{errors[name]?.message}</p>}
            </div>)}
            <p role="status" className="bb-form-note">{isPending ? 'Submitting your offer request…' : ''}</p>
            {submitError && <p className="bb-error" role="alert">{submitError}</p>}
            <button className="bb-submit" type="submit" disabled={isPending}>{isPending ? 'Submitting…' : 'Get my 10% off'}</button>
            <p className="bb-security"><LockKeyhole aria-hidden="true" /> Your details are secure and will never be shared.</p>
          </form>
        </section>}

        <div className="bb-benefits">{benefits.map(({ Icon, lines }) => <div key={lines[0]}><Icon aria-hidden="true" /><p>{lines[0]}<br />{lines[1]}</p></div>)}</div>
        {!thankYou && <p className="bb-terms">Offer valid for a limited time. One use per transaction.<br />Not to be used in conjunction with any other offer.</p>}
        <footer className="bb-footer">
          <div className="bb-contact"><span><MapPin aria-hidden="true" /> Main Street, Bainsford, Falkirk FK2 7NU</span><a href="tel:01324636330"><Phone aria-hidden="true" />01324 636330</a><Link to="/">thecreamerysportsbar.com</Link></div>
          {thankYou && <p className="bb-footer-motto">Good Food · Great Drinks · Friendly Vibes</p>}
          <div className="bb-legal"><Link to="/privacy">Privacy Policy</Link><Link to="/terms">Terms of Service</Link></div>
        </footer>
      </div>
    </main>
  );
};

export default BigBreakfastPage;
